import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { ColumnProps } from 'antd/lib/table';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import DeviceActionMenu from '@totem/components/devices/DeviceActionMenu';
import DeviceContext from '@totem/components/devices/devicesContainer/deviceContainerContext';
import { isManaged } from '@totem/components/devices/devicesContainer/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DeviceQueryResult } from '@totem/types/devices';
import { nullifyIfEmpty } from '@totem/utilities/tableFilterUtilities';

import '../devices.css';

type Props = {
  extraColumns?: ColumnProps<DeviceQueryResult>[];
  hideActions?: boolean;
};

const DeviceOrgTable = ({ extraColumns, hideActions }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { loading, deviceData, totalRecords } = useContext(DeviceContext);
  const { input, handleTableChange, getFilterValues, getFilterOptions } =
    useContext(InputContext);

  const getBuildingDisplay = (device: DeviceQueryResult) => {
    if (
      device.building !== null &&
      device.building.id !== null &&
      device.building.id !== EMPTY_ID
    ) {
      return (
        <Link to={`/dashboard/buildings/${device.building.id}`}>
          {device.building.name}
        </Link>
      );
    } else if (device.building !== null) {
      return <span>{device.building.name}</span>;
    }
    return <span>-</span>;
  };

  const getControlSystemDisplay = (device: DeviceQueryResult) => {
    if (
      device.controlSystem !== null &&
      device.controlSystem.id !== null &&
      device.controlSystem.id !== EMPTY_ID
    ) {
      return (
        <Link to={`/dashboard/controlsystems/${device.controlSystem.id}`}>
          {device.controlSystem.name}
        </Link>
      );
    } else if (device.controlSystem !== null) {
      return <span>{device.controlSystem.name}</span>;
    }
    return <span>-</span>;
  };

  const columns: ColumnProps<DeviceQueryResult>[] = [
    {
      title: 'Organization',
      dataIndex: 'organization.name',
      key: 'organization',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <span>{device.organization.name}</span>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.organization.name === compB.organization.name
          ? 0
          : compA.organization.name > compB.organization.name
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('organization'),
      filters: getFilterOptions('organization'),
    },
    {
      title: 'Name',
      dataIndex: 'device.displayName',
      key: 'displayName',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <Link to={`/dashboard/devices/${device.device.id}`}>
          {device.device.displayName}
        </Link>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.device.displayName === compB.device.displayName
          ? 0
          : compA.device.displayName > compB.device.displayName
            ? 1
            : -1,
    },
    {
      title: 'Type',
      dataIndex: 'device.type',
      key: 'type',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => device.device.type,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.device.type === compB.device.type
          ? 0
          : compA.device.type > compB.device.type
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('type'),
      filters: getFilterOptions('type'),
    },
    {
      title: 'Vendor',
      dataIndex: 'device.vendor',
      key: 'vendor',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => device.device.vendor,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.device.vendor === compB.device.vendor
          ? 0
          : compA.device.vendor > compB.device.vendor
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('vendor'),
      filters: getFilterOptions('vendor'),
    },
    {
      title: 'OS',
      dataIndex: 'device.operatingSystem',
      key: 'operatingSystem',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => device.device.operatingSystem,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.device.operatingSystem === compB.device.operatingSystem
          ? 0
          : compA.device.operatingSystem > compB.device.operatingSystem
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('operatingSystem'),
      filters: getFilterOptions('operatingSystem'),
    },
    {
      title: 'IP Addresses',
      dataIndex: 'device.ipAddresses',
      key: 'ipAddresses',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <span>
          {device.device.ipAddresses !== null &&
            device.device.ipAddresses.map((addr, idx) =>
              idx === 0 ? (
                <span key={addr}>{addr}</span>
              ) : (
                <span key={addr}>
                  <br />
                  {addr}
                </span>
              ),
            )}
        </span>
      ),
    },
    {
      title: 'Region',
      dataIndex: 'region.name',
      key: 'region',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => device.region.name,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.region.name === compB.region.name
          ? 0
          : compA.region.name > compB.region.name
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('region'),
      filters: getFilterOptions('region'),
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => getBuildingDisplay(device),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.building.name === compB.building.name
          ? 0
          : compA.building.name > compB.building.name
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('building'),
      filters: getFilterOptions('building'),
    },
    {
      title: 'Control System',
      dataIndex: 'controlSystem.name',
      key: 'controlSystemId',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => getControlSystemDisplay(device),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.controlSystem.name === compB.controlSystem.name
          ? 0
          : compA.controlSystem.name > compB.controlSystem.name
            ? 1
            : -1,
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'additionalOptions',
      render: (_, device: DeviceQueryResult) => (
        <div className="center-table-cell">
          <DeviceActionMenu device={device} />
        </div>
      ),
    },
  ];

  const getColumns = () => {
    const cols = [...columns];

    if (permissions.systemAdministrator.hasPermissions(userProfile)) {
      cols.splice(5, 0, {
        title: (
          <IBTooltip content={permissions.systemAdministrator.tooltip}>
            Managed
          </IBTooltip>
        ),
        dataIndex: 'device.flags.managed.status',
        key: 'isManaged',
        showSorterTooltip: true,
        render: (_, device: DeviceQueryResult) =>
          isManaged(device) ? (
            <CheckCircleOutlined
              style={{
                color: 'green',
                fontSize: '20px',
              }}
            />
          ) : (
            <MinusCircleOutlined
              style={{
                fontSize: '20px',
              }}
            />
          ),
        sortDirections: ['ascend', 'descend'],
        sorter: (compA, compB) =>
          compA.device.flags.managed.status ===
          compB.device.flags.managed.status
            ? 0
            : compA.device.flags.managed.status >
                compB.device.flags.managed.status
              ? 1
              : -1,
        filterMultiple: false,
        filteredValue: nullifyIfEmpty(getFilterValues('isManaged')),
        filters: [
          {
            text: (
              <span>
                <CheckCircleOutlined
                  style={{
                    color: 'green',
                    fontSize: '20px',
                    marginRight: '15px',
                  }}
                />
                &nbsp;Is Managed
              </span>
            ),
            value: 'ACTIVE',
          },
          {
            text: (
              <span>
                <MinusCircleOutlined
                  style={{
                    fontSize: '20px',
                    marginRight: '15px',
                  }}
                />
                &nbsp;Not Managed
              </span>
            ),
            value: 'INACTIVE',
          },
        ],
      });

      cols.splice(6, 0, {
        title: (
          <IBTooltip content={permissions.systemAdministrator.tooltip}>
            Monitored
          </IBTooltip>
        ),
        dataIndex: 'device.flags.monitored.status',
        key: 'isMonitored',
        showSorterTooltip: true,
        render: (_, device: DeviceQueryResult) =>
          device.device.flags.monitored.status === 'ACTIVE' ? (
            <CheckCircleOutlined
              style={{
                color: 'green',
                fontSize: '20px',
              }}
            />
          ) : (
            <MinusCircleOutlined
              style={{
                fontSize: '20px',
              }}
            />
          ),
        sortDirections: ['ascend', 'descend'],
        sorter: (compA, compB) =>
          compA.device.flags.monitored.status ===
          compB.device.flags.monitored.status
            ? 0
            : compA.device.flags.monitored.status >
                compB.device.flags.monitored.status
              ? 1
              : -1,
        filterMultiple: false,
        filteredValue: nullifyIfEmpty(getFilterValues('isMonitored')),
        filters: [
          {
            text: (
              <span>
                <CheckCircleOutlined
                  style={{
                    color: 'green',
                    fontSize: '20px',
                    marginRight: '15px',
                  }}
                />
                &nbsp;Is Monitored
              </span>
            ),
            value: 'ACTIVE',
          },
          {
            text: (
              <span>
                <MinusCircleOutlined
                  style={{
                    fontSize: '20px',
                    marginRight: '15px',
                  }}
                />
                &nbsp;Not Monitored
              </span>
            ),
            value: 'INACTIVE',
          },
        ],
      });
    }

    if (extraColumns !== undefined && extraColumns.length > 0) {
      cols.splice(cols.length - 1, 0, ...extraColumns);
    }

    if (hideActions) {
      return cols.filter((col) => col.key !== 'additionalOptions');
    }

    return cols;
  };

  const getDevices = () => {
    if (deviceData !== null) {
      if (deviceData.devices !== null) {
        return deviceData.devices;
      }
    }
    return [];
  };

  return (
    <IBTable
      showSorterTooltip
      columns={getColumns()}
      dataSource={getDevices()}
      loading={loading}
      onChange={(updatedPagination, filters, sorter, extra) =>
        handleTableChange(updatedPagination, filters, sorter, extra)
      }
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.device.id}
    />
  );
};

export default DeviceOrgTable;
